import { useNavigate } from 'react-router-dom'
import { MapPin, Heart, MessageCircle, Sparkles, Star } from 'lucide-react'
import { Product, productApi } from '../../services/api'
import { useCartContext } from '../../contexts/CartContext'
import { useState } from 'react'
import { createPortal } from 'react-dom'
import { formatPrice } from '../../utils/currency'
import { logger } from '../../utils/logger'

interface ProductCardProps {
  product: Product
}

export default function ProductCard({ product }: ProductCardProps) {
  const navigate = useNavigate()
  const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : null
  const currentUser = tg?.initDataUnsafe?.user
  const { addToCart, isItemInCart, removeItem, cart } = useCartContext()
  const [isAdding, setIsAdding] = useState(false)
  const [imageError, setImageError] = useState(false)

  // Contact Seller modal state
  const [showInterestModal, setShowInterestModal] = useState(false)
  const [interestMessage, setInterestMessage] = useState('')
  const [proposedPrice, setProposedPrice] = useState('')
  const [interestQuantity, setInterestQuantity] = useState(1)
  const [isSendingInterest, setIsSendingInterest] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // A product that is no longer active (archived/inactive/etc.) is treated as sold.
  const isSold = !!product.status && product.status !== 'active'
  const inCart = isItemInCart(product.id)
  const hasDiscount = product.compare_price && product.compare_price > product.price
  const discountPercent = hasDiscount
    ? Math.round(((product.compare_price! - product.price) / product.compare_price!) * 100)
    : 0

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      setIsAdding(true)

      if (inCart) {
        // Toggle OFF: remove from favorites
        const cartItem = cart?.items?.find(i => i.product_id === product.id)
        if (cartItem) {
          await removeItem(cartItem.id)
        }
      } else {
        // Toggle ON: add to favorites
        await addToCart(product.id, 1)
      }
      if (tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('success')
      }
    } catch (error) {
      console.error('Failed to add to cart:', error)
    } finally {
      setIsAdding(false)
    }
  }

  const handleCardClick = () => {
    navigate(`/product/${product.id}`)
  }

  const handleContactSeller = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (tg?.HapticFeedback) {
      tg.HapticFeedback.impactOccurred('light')
    }
    setShowInterestModal(true)
  }

  const handleSendInterest = async () => {
    setIsSendingInterest(true)
    setError(null)

    try {
      // Fold an optional quantity into the message the seller receives.
      const qtyNote = interestQuantity > 1 ? `Quantity: ${interestQuantity}` : ''
      const finalMessage = [qtyNote, interestMessage].filter(Boolean).join(' — ') || undefined

      const authToken = localStorage.getItem('auth_token')
      const hasValidAuth = authToken && authToken.length > 20 && !authToken.startsWith('telegram:')

      if (hasValidAuth) {
        const price = proposedPrice ? parseFloat(proposedPrice) : undefined
        await productApi.sendProductInterest(product.id, finalMessage, price)
      } else {
        const visitorData: any = {}
        if (tg?.initDataUnsafe?.user) {
          const tgUser = tg.initDataUnsafe.user
          visitorData.telegram_id = tgUser.id
          visitorData.telegram_user = tgUser.username
          visitorData.visitor_name = tgUser.first_name || tgUser.username || undefined
        }
        if (finalMessage) visitorData.message = finalMessage
        if (proposedPrice) visitorData.proposed_price = parseFloat(proposedPrice)
        await productApi.sendPublicProductInterest(product.id, visitorData)
      }

      if (tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('success')
      }

      setSuccessMessage('Your interest has been sent to the seller!')
      setError(null)

      setTimeout(() => {
        setShowInterestModal(false)
        setInterestMessage('')
        setProposedPrice('')
        setSuccessMessage(null)
      }, 2000)
    } catch (error) {
      logger.error('ProductCard: Failed to send interest:', error)
      setError(error instanceof Error ? error.message : 'Failed to send interest')
      setSuccessMessage(null)
      if (tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('error')
      }
    } finally {
      setIsSendingInterest(false)
    }
  }

  return (
    <div
      onClick={handleCardClick}
      className="group relative bg-white rounded-2xl overflow-hidden
        shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.10)]
        active:scale-[0.98] transition-all duration-200 cursor-pointer"
    >
      {/* Image container */}
      <div className="aspect-[4/3] relative bg-gray-100 overflow-hidden">
        {product.image_url && !imageError ? (
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={() => setImageError(true)}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
            <Sparkles className="w-10 h-10 text-blue-300" />
          </div>
        )}

        {/* Price tag - floating on image */}
        <div className="absolute bottom-2 left-2">
          <div className="bg-white/95 backdrop-blur-sm rounded-xl px-3 py-1.5
            shadow-[0_2px_8px_rgba(0,0,0,0.12)]">
            <span className="font-bold text-gray-900 text-sm">
              {formatPrice(product.price, product.currency)}
            </span>
            {hasDiscount && (
              <span className="text-gray-400 text-xs line-through ml-1.5">
                {formatPrice(product.compare_price!, product.currency)}
              </span>
            )}
          </div>
        </div>

        {/* Discount badge (hidden when sold) */}
        {hasDiscount && !isSold && (
          <div className="absolute top-2 left-2 bg-red-500 text-white
            text-[11px] font-bold px-2 py-0.5 rounded-lg shadow-lg">
            -{discountPercent}%
          </div>
        )}

        {/* Sold / unavailable overlay */}
        {isSold && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <span className="bg-gray-900/85 text-white text-[11px] font-bold uppercase
              tracking-wide px-3 py-1.5 rounded-lg shadow-lg">
              Sold
            </span>
          </div>
        )}

        {/* Quick actions overlay on hover/tap */}
        <div className="absolute top-2 right-2 flex flex-col gap-1.5
          opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <button
            onClick={handleAddToCart}
            disabled={isAdding}
            className={`w-9 h-9 rounded-xl flex items-center justify-center
              backdrop-blur-md shadow-lg transition-all duration-200
              active:scale-90 ${inCart
                ? 'bg-green-500/90 text-white'
                : 'bg-white/90 text-gray-700 hover:bg-white'}`}
          >
            <Heart className={`w-4 h-4 ${inCart ? 'fill-current' : ''}`} />
          </button>
          {!isSold && (
            <button
              onClick={handleContactSeller}
              className="w-9 h-9 rounded-xl flex items-center justify-center
                bg-white/90 text-gray-700 backdrop-blur-md shadow-lg
                hover:bg-white active:scale-90 transition-all duration-200"
            >
              <MessageCircle className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Product info */}
      <div className="p-3">
        <h3 className="text-gray-800 font-medium text-sm leading-snug line-clamp-2 mb-1.5">
          {product.name}
        </h3>

        {/* Location, condition & rating — must never overflow the grid cell:
            the city name truncates while condition and rating stay intact. */}
        <div className="flex items-center gap-2 text-gray-400 text-xs min-w-0">
          {product.city_name && (
            <span className="inline-flex items-center gap-1 min-w-0">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{product.city_name}</span>
            </span>
          )}
          {product.condition && (
            <span className="text-gray-300 flex-shrink-0">·</span>
          )}
          {product.condition && (
            <span className="capitalize flex-shrink-0">{product.condition}</span>
          )}
          {/* Seller rating */}
          {product.owner && (product.owner as any).avg_seller_rating > 0 && (
            <>
              <span className="text-gray-300 flex-shrink-0">·</span>
              <span className="inline-flex items-center gap-0.5 text-amber-500 flex-shrink-0">
                <Star className="w-3 h-3 fill-current" />
                <span>{(product.owner as any).avg_seller_rating}</span>
              </span>
            </>
          )}
        </div>
      </div>

      {/* Bottom action bar */}
      <div className="px-3 pb-3 flex gap-2">
        <button
          onClick={handleAddToCart}
          disabled={isAdding}
          className={`flex-1 py-2 rounded-xl text-xs font-semibold transition-all duration-200
            active:scale-95 flex items-center justify-center gap-1.5
            ${inCart
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'}`}
        >
          <Heart className={`w-3.5 h-3.5 ${inCart ? 'fill-green-600 text-green-600' : ''}`} />
          {inCart ? 'Saved' : 'Save'}
        </button>
        {isSold ? (
          <div className="flex-1 py-2 rounded-xl text-xs font-semibold border border-gray-200
            bg-gray-100 text-gray-400 flex items-center justify-center">
            Unavailable
          </div>
        ) : (
          <button
            onClick={handleContactSeller}
            className="flex-1 py-2 rounded-xl text-xs font-semibold
              bg-blue-500 text-white hover:bg-blue-600
              transition-all duration-200 active:scale-95
              flex items-center justify-center gap-1.5"
          >
            <MessageCircle className="w-3.5 h-3.5" />
            Contact
          </button>
        )}
      </div>

      {/* Interest Modal */}
      {showInterestModal && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center p-4 z-[9999]"
          onClick={(e) => {
            if (e.target === e.currentTarget && !isSendingInterest) {
              setShowInterestModal(false)
              setInterestMessage('')
              setProposedPrice('')
              setError(null)
              setSuccessMessage(null)
            }
          }}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl animate-slide-up"
            onClick={(e) => e.stopPropagation()}>
            {successMessage ? (
              /* Success state */
              <div className="text-center py-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                  <span className="text-3xl">✅</span>
                </div>
                <h2 className="text-xl font-bold text-gray-800 mb-2">Interest Sent! 🎉</h2>
                <p className="text-gray-500 text-sm leading-relaxed">
                  The seller will receive your message via Telegram.
                  {currentUser?.first_name && (
                    <> They'll see <strong>{currentUser.first_name}</strong> and can contact you directly.</>
                  )}
                </p>
                <button
                  onClick={() => { setShowInterestModal(false); setSuccessMessage(null) }}
                  className="mt-6 bg-blue-500 text-white px-8 py-2.5 rounded-xl font-semibold text-sm
                    hover:bg-blue-600 transition-colors"
                >
                  Done
                </button>
              </div>
            ) : (
              /* Form state */
              <>
                <h2 className="text-xl font-bold text-gray-800 mb-1">Send Interest</h2>
                <p className="text-gray-500 text-sm mb-4">
                  The seller will be notified via Telegram.
                </p>

                <div className="mb-4 bg-blue-50 rounded-xl p-3 text-sm text-blue-900">
                  Seller will see: <strong>{currentUser?.first_name || currentUser?.username || 'Your Name'}</strong>
                  {currentUser?.username && ` (@${currentUser.username})`}
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Quantity
                  </label>
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={() => setInterestQuantity((q: number) => Math.max(1, q - 1))}
                      disabled={interestQuantity <= 1}
                      className="w-10 h-10 rounded-xl border border-gray-200 bg-gray-50 text-gray-700
                        text-lg font-semibold active:scale-90 transition disabled:opacity-40">−</button>
                    <span className="w-8 text-center text-gray-800 font-semibold">{interestQuantity}</span>
                    <button type="button" onClick={() => setInterestQuantity((q: number) => Math.min(99, q + 1))}
                      className="w-10 h-10 rounded-xl border border-gray-200 bg-gray-50 text-gray-700
                        text-lg font-semibold active:scale-90 transition">+</button>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Proposed Price <span className="text-gray-400 font-normal">— Optional</span>
                  </label>
                  <input type="number" value={proposedPrice}
                    onChange={(e) => setProposedPrice(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50
                      text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400
                      focus:border-transparent text-sm"
                    placeholder={`Offer (listed: ${formatPrice(product.price, product.currency)})`}
                    min="0" step="0.01" />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Message <span className="text-gray-400 font-normal">— Optional</span>
                  </label>
                  <textarea value={interestMessage}
                    onChange={(e) => setInterestMessage(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50
                      text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400
                      focus:border-transparent text-sm resize-none"
                    placeholder="Hi! Is this still available? I'm interested..."
                    rows={3} />
                </div>

                {error && (
                  <div className="mb-4 bg-red-50 rounded-xl p-3 text-sm text-red-600">{error}</div>
                )}
              </>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowInterestModal(false)
                  setInterestMessage('')
                  setProposedPrice('')
                  setError(null)
                  setSuccessMessage(null)
                }}
                disabled={isSendingInterest || !!successMessage}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600
                  font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSendInterest}
                disabled={isSendingInterest || !!successMessage}
                className="flex-1 py-3 rounded-xl bg-blue-500 text-white font-medium
                  hover:bg-blue-600 transition-colors disabled:opacity-50 text-sm"
              >
                {isSendingInterest ? 'Sending...' : successMessage ? '✓ Sent!' : 'Send Interest'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
